import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { setLoader } from './loader.slice'
import mainApiService from '../../Services'
import { CONTAINER_MANAGEMENT_CRUD } from '../../Constants/ApiDefinations/index'
import {
  IManageContainerSlice,
  IContainerInstanceState,
  initialContainerInstanceState,
} from '../../Models/ManageContainer'
import { IPinnedItemDetailInRedux } from '../../Models/DataCategoryUtils'

const initialState: IManageContainerSlice = {
  containersById: {},
}

const ensureContainer = (state: IManageContainerSlice, containerId: string) => {
  if (!state.containersById[containerId]) {
    state.containersById[containerId] = { ...initialContainerInstanceState }
  }
}

const manageContainerSlice = createSlice({
  name: 'manageContainer',
  initialState,
  reducers: {
    setContainerDefinitionDetails: (
      state,
      action: PayloadAction<{ containerId: string; data: any }>,
    ) => {
      const { containerId, data } = action.payload
      ensureContainer(state, containerId)
      state.containersById[containerId].containerDefinitionDetails = { ...data }
    },
    setContainerDetails: (state, action: PayloadAction<{ containerId: string; data: any }>) => {
      const { containerId, data } = action.payload
      ensureContainer(state, containerId)
      state.containersById[containerId].containerDetails = { ...data }
    },
    setParentContainerDetail: (
      state,
      action: PayloadAction<{ containerId: string; data: any }>,
    ) => {
      const { containerId, data } = action.payload
      ensureContainer(state, containerId)
      state.containersById[containerId].parentContainerDetail = { ...data }
    },
    setContainerItemDetail: (
      state,
      action: PayloadAction<{ containerId: string; data: { [key: string]: any[] } }>,
    ) => {
      const { containerId, data } = action.payload
      ensureContainer(state, containerId)
      state.containersById[containerId].itemDetail = { ...data }
    },
    setContainerPinnedItemDetail: (
      state,
      action: PayloadAction<{ containerId: string; data: IPinnedItemDetailInRedux }>,
    ) => {
      const { containerId, data } = action.payload
      ensureContainer(state, containerId)
      state.containersById[containerId].containerPinnedItems = data
    },
    setMasterLayoutItems: (state, action: PayloadAction<{ containerId: string; data: any[] }>) => {
      const { containerId, data } = action.payload
      ensureContainer(state, containerId)
      state.containersById[containerId].masterLayoutItemInfo = data
    },
    resetContainerData: (state, action: PayloadAction<string | undefined>) => {
      if (action.payload) {
        delete state.containersById[action.payload]
      } else {
        state.containersById = {}
      }
    },
  },
})
export const {
  setContainerDefinitionDetails,
  setContainerDetails,
  setParentContainerDetail,
  resetContainerData,
  setContainerItemDetail,
  setContainerPinnedItemDetail,
  setMasterLayoutItems,
} = manageContainerSlice.actions
export default manageContainerSlice

export const manageContainerSelector = (state: any) => state.manageContainer

export const makeContainerDetailSelector = (containerId: string) =>
  createSelector(
    [(state: any) => state.manageContainer.containersById?.[containerId]],
    (containerState: IContainerInstanceState | undefined) => containerState?.containerDetails ?? {},
  )

export const makeContainerDefinitionDetailSelector = (containerId: string) =>
  createSelector(
    [(state: any) => state.manageContainer.containersById?.[containerId]],
    (containerState: IContainerInstanceState | undefined) =>
      containerState?.containerDefinitionDetails ?? {},
  )

export const makeParentContainerDetailSelector = (containerId: string) =>
  createSelector(
    [(state: any) => state.manageContainer.containersById?.[containerId]],
    (containerState: IContainerInstanceState | undefined) =>
      containerState?.parentContainerDetail ?? {},
  )

export const makeItemDetailSelector = (containerId: string) =>
  createSelector(
    [(state: any) => state.manageContainer.containersById?.[containerId]],
    (containerState: IContainerInstanceState | undefined) => containerState?.itemDetail ?? {},
  )

export const makeContainerPinnedItemsSelector = (containerId: string) =>
  createSelector(
    [(state: any) => state.manageContainer.containersById?.[containerId]],
    (containerState: IContainerInstanceState | undefined) =>
      containerState?.containerPinnedItems ?? { ids: [], position: [], items: [] },
  )

export const makeMasterLayoutItemsSelector = (containerId: string) =>
  createSelector(
    [(state: any) => state.manageContainer.containersById?.[containerId]],
    (containerState: IContainerInstanceState | undefined) =>
      containerState?.masterLayoutItemInfo ?? [],
  )

export const getContainerDefinitionDetailById = createAsyncThunk(
  'manageContainer/getContainerDefinitionDetailById',
  async ({ id, containerId }: { id: string; containerId: string }, { dispatch }) => {
    dispatch(setLoader(true))
    const requestedData: ContainerCRUDAPIDefinitionModal =
      CONTAINER_MANAGEMENT_CRUD.GET_CONTAINER_DEFINITION_DETAIL_BY_ID(id)
    const result: any = await mainApiService(requestedData)
    dispatch(setContainerDefinitionDetails({ containerId, data: result?.data }))
    dispatch(setLoader(false))
    return result
  },
)
export const getContainerDetailById = createAsyncThunk(
  'manageContainer/getContainerDetailById',
  async ({ id, containerId }: { id: string; containerId: string }, { dispatch }) => {
    dispatch(setLoader(true))
    const requestedData: ContainerCRUDAPIDefinitionModal =
      CONTAINER_MANAGEMENT_CRUD.GET_CONTAINER_DETAIL_BY_ID(id)
    const result: any = await mainApiService(requestedData)
    dispatch(setContainerDetails({ containerId, data: result?.data }))
    dispatch(setLoader(false))
    return result
  },
)
export const getContainerListAPICall = createAsyncThunk(
  'manageContainer/containerList',
  async (payload: any, { dispatch }) => {
    const requestedData: any = CONTAINER_MANAGEMENT_CRUD.LISTING(payload)
    const data: any = await mainApiService(requestedData)
    return data
  },
)

export interface ContainerCRUDAPIDefinitionModal {
  url: string
  method: string
  data: any
}
